// src/App.js
import NotificationPrompt from './components/NotificationPrompt';
import InstallPrompt from './components/InstallPrompt';
import CountySelector from './components/CountySelector';
import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import NavBar from './components/NavBar';
import VoterLookup from './pages/VoterLookup';
import DistrictMaps from './pages/DistrictMaps';
import Candidates from './pages/Candidates';
import Volunteer from './pages/Volunteer';
import Events from './pages/Events';
import Resources from './pages/Resources';
import WhereToVote from './pages/WhereToVote';
import BallotInitiatives from './pages/BallotInitiatives';
import Admin from './pages/Admin';
import { detectCountyId, getCounty } from './config/counties';
import { upsertSubscriberToken } from './utils/airtable';
import './App.css';

const VALID_TABS = ['lookup', 'maps', 'candidates', 'volunteer', 'events', 'resources', 'wheretovote', 'ballot', 'admin'];

// Reads the tab straight from the URL hash — independent of county, so it
// resolves the same way on any subdomain (weld.votergo.app, larimer.votergo.app,
// or localhost with ?county=).
const getTabFromHash = () => {
  const hash = window.location.hash.replace('#', '').toLowerCase().trim();
  return VALID_TABS.includes(hash) ? hash : 'lookup';
};

function App() {
  const [countyId] = useState(() => detectCountyId());
  const county = getCounty(countyId);

  const [activeTab, setActiveTab] = useState(getTabFromHash);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [sharedAddress, setSharedAddress] = useState('');

  // Editing the URL bar to only change the hash (e.g. appending #admin) does
  // not reload the page, so the tab has to be re-derived on 'hashchange' too —
  // otherwise it's stuck on whatever tab was active when the app first mounted.
  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

const handleNotificationToken = async (token) => {
    try {
      await upsertSubscriberToken(token);
    } catch (err) {
      console.log('Could not save token:', err);
    }
  };

  useEffect(() => {
    if (!county) return;
    // Load Google Maps dynamically using the environment variable
    if (window.google) { setMapsLoaded(true); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places,geocoding`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

useEffect(() => {
  if (!county) return;
  window.location.hash = activeTab;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab]);

  useEffect(() => {
    if (county) document.title = `VoterGO — ${county.name} Republican Voter Hub`;
  }, [county]);

  if (!county) {
    return <CountySelector />;
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'lookup':     return <VoterLookup mapsLoaded={mapsLoaded} setActiveTab={setActiveTab} setSharedAddress={setSharedAddress} county={county} />;
      case 'maps':       return <DistrictMaps mapsLoaded={mapsLoaded} initialAddress={sharedAddress} county={county} />;
      case 'candidates': return <Candidates county={county} />;
      case 'volunteer':  return <Volunteer county={county} />;
      case 'events':     return <Events county={county} />;
      case 'resources':  return <Resources county={county} />;
      case 'wheretovote': return <WhereToVote county={county} mapsLoaded={mapsLoaded} />;
      case 'ballot':     return <BallotInitiatives county={county} />;
      case 'admin': return <Admin county={county} />;
      default:           return <VoterLookup mapsLoaded={mapsLoaded} county={county} />;
    }
  };

  return (
    <div className="app">
      <div className="stripe" />
      <Header tagline={`${county.name} Republican Voter Hub`} />
      <NavBar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="content">
        {renderTab()}
      </main>
      <div className="stripe" />
      <InstallPrompt />
      <NotificationPrompt onTokenReceived={handleNotificationToken} county={county} />
      <footer className="app-footer">
      <div>
        This app is provided by the {county.orgName}
        {county.orgAddress && <> &nbsp;|&nbsp; {county.orgAddress}</>}
        {county.orgWebsite && <> &nbsp;|&nbsp; <a href={county.orgWebsite} target="_blank" rel="noreferrer">{county.orgWebsite.replace('https://www.', '').replace('https://', '')}</a></>}
      </div>
      <div style={{marginTop: 4}}>
      © {new Date().getFullYear()} VoterGO, LLC. All rights reserved. VoterGO™ is a trademark of VoterGO, LLC. &nbsp;|&nbsp; <a               href="https://www.votergo.app" target="_blank" rel="noreferrer">votergo.app</a>
     </div>
     </footer>
    </div>
  );
}

export default App;
